import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, firstValueFrom, of } from 'rxjs';

interface RoleRule {
  /** When true, this role can access every route. */
  allowAll?: boolean;
  /** Explicit list of route paths this role may access (e.g. "home", "domain-catalogs"). */
  routes?: string[];
}

interface RoleAccessConfig {
  roles: Record<string, RoleRule>;
  /** Role assumed when the user has no role matching the map. */
  defaultRole?: string;
  /** Where to send users who try to open a route they can't access. */
  fallbackRoute?: string;
}

const EMPTY_CONFIG: RoleAccessConfig = { roles: {}, fallbackRoute: 'home' };

@Injectable({ providedIn: 'root' })
export class RoleAccessService {
  private loadPromise?: Promise<void>;
  private config: RoleAccessConfig = EMPTY_CONFIG;
  /** role -> list of emails (temporary email-based role assignment). */
  private userRoleMap: Record<string, string[]> = {};

  constructor(private readonly http: HttpClient) {}

  /** Loads (once) and caches both the role-access map and the email→role map. */
  load(): Promise<void> {
    if (!this.loadPromise) {
      this.loadPromise = Promise.all([
        firstValueFrom(
          this.http.get<RoleAccessConfig>('assets/config/role-access.json').pipe(catchError(() => of(EMPTY_CONFIG))),
        ),
        firstValueFrom(
          this.http.get<Record<string, string[]>>('assets/config/user-role-map.json').pipe(catchError(() => of({}))),
        ),
      ]).then(([access, userMap]) => {
        this.config = access || EMPTY_CONFIG;
        this.userRoleMap = userMap || {};
      });
    }
    return this.loadPromise;
  }

  get fallbackRoute(): string {
    return this.config.fallbackRoute || 'home';
  }

  private activeRoles: string[] = [];

  setCurrentRoles(roles: string[]): void {
    this.activeRoles = [...roles];
  }

  getCurrentRoles(): string[] {
    return this.activeRoles;
  }

  /** Roles assigned to the given email in user-role-map.json (case-insensitive). */
  rolesForEmail(email: string): string[] {
    if (!email) return [];
    const lower = email.toLowerCase();
    return Object.entries(this.userRoleMap)
      .filter(([, emails]) => (emails || []).some((e) => String(e).toLowerCase() === lower))
      .map(([role]) => role);
  }

  /**
   * Resolves the effective roles for a user: email-based assignment takes
   * priority (temporary), otherwise the Keycloak token roles are used.
   */
  resolveRoles(email: string, tokenRoles: string[] = []): string[] {
    const emailRoles = this.rolesForEmail(email);
    return emailRoles.length ? emailRoles : tokenRoles;
  }

  /**
   * Returns true if any of the user's roles grants access to the given route
   * path. If the user has no role present in the map, the configured
   * `defaultRole` is used as a fallback.
   */
  canAccess(path: string, roles: string[]): boolean {
    const map = this.config.roles || {};
    // No role map configured (or it failed to load) → don't block anyone.
    if (!Object.keys(map).length) return true;
    const matched = roles.map((role) => map[role]).filter(Boolean) as RoleRule[];
    const rules = matched.length
      ? matched
      : this.config.defaultRole && map[this.config.defaultRole]
        ? [map[this.config.defaultRole]]
        : [];

    if (!rules.length) return false;
    return rules.some((rule) => rule.allowAll === true || (rule.routes || []).includes(path));
  }
}
